import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  CaretRight, CaretDown, ArrowsClockwise, FacebookLogo, Image as ImageIcon,
  Funnel, MagnifyingGlass, ArrowSquareOut, ArrowUp, ArrowDown,
  PlayCircle, Sparkle, Table as TableIcon,
} from "@phosphor-icons/react";
import CreativeDashboard from "./CreativeDashboard";
import CommerceInsights from "./CommerceInsights";
import IntegrationStatus from "./IntegrationStatus";
import FreshnessBar from "./intelligence/FreshnessBar";
import WidgetIntelligence from "./intelligence/WidgetIntelligence";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtNum = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
const fmtMoney = (n, c = "USD") => {
  if (!n && n !== 0) return "-";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: c, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${c} ${fmtNum(n)}`;
  }
};
const fmtPct = (n) => (n === undefined || n === null ? "-" : `${Number(n).toFixed(2)}%`);

const STATUS_COLORS = {
  ACTIVE: { bg: "#16A34A22", color: "#86EFAC", label: "Active" },
  PAUSED: { bg: "#71717A33", color: "#D4D4D8", label: "Paused" },
  DELETED: { bg: "#FF444422", color: "#FF6666", label: "Deleted" },
  ARCHIVED: { bg: "#3F3F4633", color: "#A1A1AA", label: "Archived" },
  CAMPAIGN_PAUSED: { bg: "#FFEB0022", color: "#FFEB00", label: "Campaign Paused" },
  ADSET_PAUSED: { bg: "#FFEB0022", color: "#FFEB00", label: "AdSet Paused" },
  IN_PROCESS: { bg: "#3B82F622", color: "#93C5FD", label: "In Process" },
  WITH_ISSUES: { bg: "#FF444422", color: "#FF6666", label: "Issues" },
  PENDING_REVIEW: { bg: "#3B82F622", color: "#93C5FD", label: "Pending Review" },
  DISAPPROVED: { bg: "#FF444422", color: "#FF6666", label: "Disapproved" },
};

const FORMAT_BADGES = {
  image: { bg: "#3B82F622", color: "#93C5FD" },
  video: { bg: "#A855F722", color: "#D8B4FE" },
  reel: { bg: "#EC489922", color: "#F9A8D4" },
  carousel: { bg: "#F59E0B22", color: "#FCD34D" },
  catalog: { bg: "#10B98122", color: "#6EE7B7" },
  story: { bg: "#06B6D422", color: "#67E8F9" },
  unknown: { bg: "#27272A", color: "#A1A1AA" },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_COLORS[status] || { bg: "#27272A", color: "#A1A1AA", label: status || "—" };
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap"
      style={{ background: meta.bg, color: meta.color }}
    >
      {meta.label}
    </span>
  );
};

const FormatBadge = ({ fmt }) => {
  if (!fmt || fmt === "unknown") return null;
  const m = FORMAT_BADGES[fmt] || FORMAT_BADGES.unknown;
  return (
    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase" style={{ background: m.bg, color: m.color }}>
      {fmt}
    </span>
  );
};

// Column definitions used for both header sorting and metric rendering
const METRIC_COLS = [
  { key: "spend", label: "Spend", type: "money", source: "meta" },
  { key: "impressions", label: "Impr.", type: "num", source: "meta" },
  { key: "clicks", label: "Clicks", type: "num", source: "meta" },
  { key: "ctr", label: "CTR", type: "pct", source: "meta" },
  { key: "cpc", label: "CPC", type: "money", source: "meta" },
  { key: "cpm", label: "CPM", type: "money", source: "meta" },
  { key: "reach", label: "Reach", type: "num", source: "meta" },
  { key: "frequency", label: "Freq.", type: "pct_raw", source: "meta" },
  { key: "purchases", label: "Purchases", type: "num", source: "meta" },
  { key: "add_to_cart", label: "ATC", type: "num", source: "meta" },
  { key: "leads", label: "Leads", type: "num", source: "meta" },
];

const SHOPIFY_COLS = [
  { key: "shopify_revenue", label: "Shop Revenue", type: "money" },
  { key: "shopify_orders", label: "Shop Orders", type: "num" },
  { key: "shopify_roas", label: "Shop ROAS", type: "pct_raw" },
];

const CONFIDENCE_COLORS = {
  high: { bg: "#10B98122", color: "#6EE7B7", label: "High" },
  medium: { bg: "#F59E0B22", color: "#FCD34D", label: "Medium" },
  low: { bg: "#FF444422", color: "#FF9999", label: "Low" },
  manual: { bg: "#52525B22", color: "#A1A1AA", label: "Review" },
};
const ConfidenceBadge = ({ c }) => {
  if (!c) return null;
  const m = CONFIDENCE_COLORS[c] || CONFIDENCE_COLORS.manual;
  return (
    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: m.bg, color: m.color }} title={`Mapping confidence: ${c}`}>
      {m.label}
    </span>
  );
};

// Aggregate Shopify totals for a campaign/adset by walking their child ads.
const aggShopify = (entity) => {
  if (entity.shopify) return entity.shopify;
  let revenue = 0, orders = 0, units = 0, spend = 0;
  if (entity.ad_sets) {
    for (const s of entity.ad_sets) {
      const sub = aggShopify(s);
      revenue += sub.revenue || 0; orders += sub.orders || 0; units += sub.units || 0;
      spend += (s.metrics || {}).spend || 0;
    }
  } else if (entity.ads) {
    for (const a of entity.ads) {
      const sh = a.shopify || {};
      revenue += sh.revenue || 0; orders += sh.orders || 0; units += sh.units || 0;
    }
    spend = (entity.metrics || {}).spend || 0;
  }
  return {
    revenue,
    orders,
    units,
    roas: spend > 0 && revenue > 0 ? +(revenue / spend).toFixed(2) : 0,
  };
};

const ShopifyCells = ({ entity, currency }) => {
  const sh = entity.shopify || aggShopify(entity);
  return (
    <>
      <td className="text-right data-number text-xs whitespace-nowrap px-2" style={sh.revenue > 0 ? { color: "#86EFAC" } : {}}>
        {sh.revenue > 0 ? fmtMoney(sh.revenue, currency) : "-"}
      </td>
      <td className="text-right data-number text-xs whitespace-nowrap px-2">
        {sh.orders > 0 ? fmtNum(sh.orders) : "-"}
      </td>
      <td className="text-right data-number text-xs whitespace-nowrap px-2" style={sh.roas > 0 ? { color: sh.roas >= 3 ? "#86EFAC" : sh.roas >= 1 ? "#FCD34D" : "#FF9999", fontWeight: 600 } : {}}>
        {sh.roas > 0 ? `${sh.roas.toFixed(2)}x` : "-"}
      </td>
    </>
  );
};

const renderMetric = (value, type, currency) => {
  if (value === undefined || value === null) return "-";
  if (type === "money") return fmtMoney(value, currency);
  if (type === "num") return fmtNum(value);
  if (type === "pct") return fmtPct(value);
  if (type === "pct_raw") return Number(value).toFixed(2);
  return value;
};

const SortHeader = ({ col, sortBy, sortDir, onClick, align = "right" }) => {
  const active = sortBy === col.key;
  return (
    <th
      onClick={() => onClick(col.key)}
      className={`px-2 py-2 font-semibold whitespace-nowrap cursor-pointer select-none hover:bg-[#1A1A1D] text-${align}`}
      data-testid={`sort-header-${col.key}`}
    >
      <div className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {col.label}
        {active && (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </div>
    </th>
  );
};

const Thumbnail = ({ url, format, size = 40 }) => {
  const [err, setErr] = useState(false);
  if (url && !err) {
    return (
      <div className="relative shrink-0">
        <img
          src={url}
          alt=""
          onError={() => setErr(true)}
          style={{ width: size, height: size }}
          className="rounded object-cover border border-[#27272A] bg-[#0A0A0C]"
        />
        {(format === "video" || format === "reel") && (
          <PlayCircle size={14} weight="fill" className="absolute bottom-0 right-0 text-white drop-shadow" />
        )}
      </div>
    );
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded bg-[#1F1F22] flex items-center justify-center shrink-0"
    >
      <ImageIcon size={size / 3} className="text-[#52525B]" />
    </div>
  );
};

const OpenInMeta = ({ url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    title="Open in Meta Ads Manager"
    className="text-[#1877F2] hover:text-[#4A9EFF] inline-flex items-center"
    data-testid="open-in-meta-btn"
  >
    <FacebookLogo size={12} weight="fill" />
  </a>
);

const OpenLandingPage = ({ url }) => {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`Open landing page: ${url}`}
      className="text-[#A1A1AA] hover:text-[#FFEB00] inline-flex items-center"
      data-testid="open-landing-page-btn"
    >
      <ArrowSquareOut size={11} />
    </a>
  );
};

const RowChevron = ({ open, onClick, hasChildren }) =>
  hasChildren ? (
    <button onClick={onClick} className="p-0.5 hover:bg-[#27272A] rounded shrink-0" data-testid="campaign-row-toggle">
      {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
    </button>
  ) : (
    <span className="inline-block w-4 shrink-0" />
  );

const ClientAdCampaigns = ({ clientId, currency: clientCurrency, month: parentMonth, dateRange, embedded = false, readOnly = false, paidMediaIntel, loadingIntel }) => {
  const defaultMonth = useMemo(() => {
    const d = new Date();
    return parentMonth || `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [parentMonth]);

  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fetchingCreatives, setFetchingCreatives] = useState(false);
  const [mappingShopify, setMappingShopify] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("spend");
  const [sortDir, setSortDir] = useState("desc");
  const [pickerAdId, setPickerAdId] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);

  // Live range insights (queried from Meta directly when parent activates a sub-month range).
  // Campaign tree below stays monthly because the per-campaign data is too heavy to live-query.
  const isRangeMode = !!dateRange && dateRange.preset !== "this_month";
  const [rangeInsights, setRangeInsights] = useState(null);
  const [loadingRangeInsights, setLoadingRangeInsights] = useState(false);
  const [rangeInsightsError, setRangeInsightsError] = useState(null);

  useEffect(() => {
    if (!isRangeMode) { setRangeInsights(null); return; }
    let cancelled = false;
    setLoadingRangeInsights(true);
    setRangeInsightsError(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/clients/${clientId}/facebook/range-insights`, {
          params: { start_date: dateRange.start_date, end_date: dateRange.end_date },
        });
        if (cancelled) return;
        if (r.data?.success && r.data.connected) {
          setRangeInsights(r.data.data);
        } else if (!r.data?.connected) {
          setRangeInsightsError("Meta not connected for this client");
        } else {
          setRangeInsightsError(r.data?.error || "Failed to load range insights");
        }
      } catch (e) {
        if (!cancelled) setRangeInsightsError(e.response?.data?.detail || "Failed to load range insights");
      } finally {
        if (!cancelled) setLoadingRangeInsights(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, isRangeMode, dateRange?.start_date, dateRange?.end_date]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/clients/${clientId}/integrations/status`);
        if (!cancelled) setIntegrationStatus(r.data);
      } catch {
        if (!cancelled) setIntegrationStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/facebook/campaigns`, { params: { month } });
      setData(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [clientId, month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setMonth(defaultMonth); }, [defaultMonth]);

  const sync = async () => {
    setSyncing(true);
    setError(null);
    setInfo(null);
    try {
      await axios.post(`${API}/clients/${clientId}/facebook/sync-campaigns`, null, {
        params: { month },
        timeout: 240000,
      });
      await load();
      setInfo("Sync complete");
      setTimeout(() => setInfo(null), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const fetchCreatives = async () => {
    setFetchingCreatives(true);
    setError(null);
    setInfo(null);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/facebook/sync-creatives`, null, {
        params: { month },
        timeout: 180000,
      });
      const { creatives_fetched = 0, creatives_failed = 0, ads_targeted = 0 } = res.data || {};
      if (creatives_fetched === 0 && ads_targeted > 0) {
        setError(`Meta returned no creatives for ${ads_targeted} ads (${creatives_failed} failures). This usually means the access token lacks ads_read scope or the ads are deleted/inaccessible. Check the backend logs for the exact Meta error code.`);
      } else {
        setInfo(`Fetched ${creatives_fetched} creatives (${creatives_failed} failed of ${ads_targeted} targeted)`);
        setTimeout(() => setInfo(null), 6000);
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Creative fetch failed");
    } finally {
      setFetchingCreatives(false);
    }
  };

  const buildShopifyMappings = async () => {
    setMappingShopify(true);
    setError(null);
    setInfo(null);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/facebook/build-shopify-mappings`, null, {
        params: { month },
        timeout: 600000,
      });
      const r = res.data || {};
      const tb = r.url_type_breakdown || {};
      const breakdownStr = `PDP ${tb.pdp || 0} · Collection ${tb.collection || 0} · Home ${tb.home || 0} · Sale ${tb.sale || 0} · Custom ${tb.custom || 0} · Non-Shopify ${tb.non_shopify || 0} · No-URL ${tb.no_url || 0}`;
      setInfo(
        `Classified ${r.ads_classified || 0} active ads → ${breakdownStr}. ` +
        `Revenue resolved on ${r.revenue_resolved || 0} mapped ads. ` +
        `${r.products_with_sales_in_month || 0} products had sales in ${month}.`,
      );
      setTimeout(() => setInfo(null), 15000);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Shopify mapping failed");
    } finally {
      setMappingShopify(false);
    }
  };

  const saveManualMapping = async (adId, product) => {
    try {
      await axios.put(`${API}/clients/${clientId}/facebook/ad-mapping/${adId}`, {
        product_id: product.id,
        product_handle: product.handle,
        product_title: product.title,
        product_image: product.image,
        product_type: product.product_type,
        vendor: product.vendor,
      }, { params: { month } });
      setPickerAdId(null);
      setInfo(`Linked "${product.title}" to ad`);
      setTimeout(() => setInfo(null), 4000);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Manual mapping failed");
    }
  };

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    if (!data?.campaigns) return;
    const ids = new Set();
    for (const c of data.campaigns) {
      ids.add(`c:${c.id}`);
      for (const s of c.ad_sets || []) ids.add(`s:${s.id}`);
    }
    setExpanded(ids);
  };
  const collapseAll = () => setExpanded(new Set());

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  // Resolve a sort value from either metrics or shopify subdocument
  const sortValue = (entity, key) => {
    if (key === "shopify_revenue") return (entity.shopify || {}).revenue || 0;
    if (key === "shopify_orders") return (entity.shopify || {}).orders || 0;
    if (key === "shopify_roas") return (entity.shopify || {}).roas || 0;
    return (entity.metrics || {})[key] || 0;
  };

  const sortFn = (a, b) => {
    const av = sortValue(a, sortBy);
    const bv = sortValue(b, sortBy);
    return sortDir === "asc" ? av - bv : bv - av;
  };

  const filteredCampaigns = useMemo(() => {
    if (!data?.campaigns) return [];
    const q = search.trim().toLowerCase();
    return data.campaigns
      .filter((c) => {
        if (statusFilter === "active" && c.effective_status !== "ACTIVE") return false;
        if (statusFilter === "paused" && c.effective_status === "ACTIVE") return false;
        if (q) {
          const inName = (c.name || "").toLowerCase().includes(q);
          const inAdsets = (c.ad_sets || []).some(
            (s) => (s.name || "").toLowerCase().includes(q) || (s.ads || []).some((a) => (a.name || "").toLowerCase().includes(q)),
          );
          return inName || inAdsets;
        }
        return true;
      })
      .slice()
      .sort(sortFn);
  }, [data, statusFilter, search, sortBy, sortDir]);

  const currency = data?.currency || clientCurrency || "USD";
  const totals = data?.totals;
  const adAccountId = data?.ad_account_id;
  const metaUrl = (level, id) => {
    if (!adAccountId) return "#";
    const acc = adAccountId.startsWith("act_") ? adAccountId : `act_${adAccountId}`;
    const param = { campaign: "selected_campaign_ids", adset: "selected_adset_ids", ad: "selected_ad_ids" }[level];
    return `https://business.facebook.com/adsmanager/manage/ads?act=${acc}&${param}=${id}`;
  };

  return (
    <div className="space-y-4" data-testid="client-ad-campaigns">
      {/* Integration status + Header — hidden when embedded inside Performance Intelligence */}
      {!embedded && <IntegrationStatus clientId={clientId} />}
      {!embedded && <FreshnessBar clientId={clientId} month={month} />}

      {/* Range Summary — live Meta Insights for the selected date range (when parent activates Today/7d/30d) */}
      {isRangeMode && (
        <div
          className="p-3 rounded border"
          style={{ background: "#1877F211", borderColor: "#1877F244" }}
          data-testid="meta-range-summary"
        >
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="flex items-center gap-2">
              <FacebookLogo size={14} weight="fill" style={{ color: "#1877F2" }} />
              <span className="text-[11px] font-semibold text-[#93C5FD]">
                Range Summary · {dateRange.label}
              </span>
              <span className="text-[10px] text-[#71717A]">
                ({dateRange.start_date} → {dateRange.end_date}) · live from Meta
              </span>
            </div>
            <span className="text-[10px] text-[#71717A]">
              Campaign tree below shows month <strong>{month}</strong> (can&apos;t be sliced without resyncing)
            </span>
          </div>
          {loadingRangeInsights ? (
            <div className="text-[11px] text-[#71717A] py-2">Loading live Meta insights…</div>
          ) : rangeInsightsError ? (
            <div className="text-[11px] py-2" style={{ color: "#FF8080" }}>{rangeInsightsError}</div>
          ) : rangeInsights ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2" data-testid="meta-range-tiles">
              <div className="px-2.5 py-2 rounded bg-white/[0.04] border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/50">Spend</div>
                <div className="text-sm font-mono text-white">{fmtMoney(rangeInsights.spend, clientCurrency)}</div>
              </div>
              <div className="px-2.5 py-2 rounded bg-white/[0.04] border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/50">Impressions</div>
                <div className="text-sm font-mono text-white">{fmtNum(rangeInsights.impressions)}</div>
              </div>
              <div className="px-2.5 py-2 rounded bg-white/[0.04] border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/50">Clicks</div>
                <div className="text-sm font-mono text-white">{fmtNum(rangeInsights.clicks)}</div>
              </div>
              <div className="px-2.5 py-2 rounded bg-white/[0.04] border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/50">CTR</div>
                <div className="text-sm font-mono text-white">{rangeInsights.ctr != null ? `${Number(rangeInsights.ctr).toFixed(2)}%` : "—"}</div>
              </div>
              <div className="px-2.5 py-2 rounded bg-white/[0.04] border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/50">CPC</div>
                <div className="text-sm font-mono text-white">{rangeInsights.cpc != null ? fmtMoney(rangeInsights.cpc, clientCurrency) : "—"}</div>
              </div>
              <div className="px-2.5 py-2 rounded bg-white/[0.04] border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/50">CPM</div>
                <div className="text-sm font-mono text-white">{rangeInsights.cpm != null ? fmtMoney(rangeInsights.cpm, clientCurrency) : "—"}</div>
              </div>
              <div className="px-2.5 py-2 rounded bg-white/[0.04] border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/50">Purchases</div>
                <div className="text-sm font-mono text-white">{fmtNum(rangeInsights.purchases)}</div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* AI Intelligence · Paid Media — same engine as Hero, scoped slice */}
      <WidgetIntelligence
        widget={paidMediaIntel}
        label={loadingIntel ? "AI Intelligence · Generating…" : "AI Intelligence · Paid Media"}
        testid="widget-intel-paid-media"
      />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FacebookLogo size={20} weight="fill" style={{ color: "#1877F2" }} />
          <h3 className="text-lg font-semibold">Performance Flow</h3>
          {data?.synced_at && (
            <span className="text-[10px] text-[#71717A]">
              · synced {new Date(data.synced_at).toLocaleString()}
              {data.creatives_synced_at && ` · creatives ${new Date(data.creatives_synced_at).toLocaleString()}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            max={defaultMonth}
            onChange={(e) => setMonth(e.target.value)}
            className="input-default text-xs py-1 px-2 w-36"
            data-testid="campaigns-month-picker"
          />
          {data && !readOnly && (
            <button
              onClick={fetchCreatives}
              disabled={fetchingCreatives || syncing || mappingShopify}
              className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
              style={{ background: fetchingCreatives ? "#52525B" : "#7C3AED", color: "#fff" }}
              title="Fetch thumbnails + creative for active/spending ads only"
              data-testid="fetch-creatives-btn"
            >
              <Sparkle size={14} className={fetchingCreatives ? "animate-spin" : ""} />
              {fetchingCreatives ? "Fetching creatives…" : "Fetch creatives"}
            </button>
          )}
          {data && !readOnly && (
            <button
              onClick={buildShopifyMappings}
              disabled={fetchingCreatives || syncing || mappingShopify}
              className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
              style={{ background: mappingShopify ? "#52525B" : "#10B981", color: "#fff" }}
              title="Auto-map ads to Shopify products via destination URL & fetch product revenue"
              data-testid="map-shopify-btn"
            >
              <ArrowsClockwise size={14} className={mappingShopify ? "animate-spin" : ""} />
              {mappingShopify ? "Mapping Shopify…" : "Map to Shopify"}
            </button>
          )}
          {!readOnly && (
            <button
              onClick={sync}
              disabled={syncing || fetchingCreatives}
              className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
              style={{ background: syncing ? "#52525B" : "#1877F2", color: "#fff" }}
              data-testid="sync-campaigns-btn"
            >
              <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing from Meta…" : "Sync from Meta"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 rounded text-xs flex items-center justify-between gap-2" style={{ background: "#FF444422", color: "#FF6666" }} data-testid="meta-error-banner">
          <span><strong>Meta:</strong> {error} — Shopify commerce insights below still load normally.</span>
        </div>
      )}
      {info && (
        <div className="p-2 rounded text-xs" style={{ background: "#10B98122", color: "#6EE7B7" }}>
          {info}
        </div>
      )}

      {/* Shopify commerce insights — pulls independently from Shopify regardless of Meta state.
          Hidden when embedded inside Performance Intelligence (the parent already renders the Commerce section). */}
      {!embedded && <CommerceInsights clientId={clientId} currency={currency} month={month} />}

      {totals && (
        <CreativeDashboard
          data={data}
          currency={currency}
          onPickProduct={(adId) => setPickerAdId(adId)}
          clientId={clientId}
          integrationStatus={integrationStatus}
          month={month}
        />
      )}

      {data && (
        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-[#71717A]">
            {data.campaign_count} campaigns · {data.adset_count} ad sets · {data.ad_count} ads
          </div>
          <button
            onClick={() => setShowTable(!showTable)}
            className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 bg-[#27272A] hover:bg-[#3F3F46] text-[#E4E4E7]"
            data-testid="toggle-detail-table"
          >
            <TableIcon size={14} />
            {showTable ? "Hide detailed table" : "Show detailed table (audit view)"}
          </button>
        </div>
      )}

      {showTable && (<>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            placeholder="Search campaign / ad set / ad…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-default text-xs py-1 pl-7 pr-2 w-64"
            data-testid="campaigns-search"
          />
        </div>
        <div className="flex items-center gap-1 ml-2">
          <Funnel size={12} className="text-[#71717A]" />
          {[
            { k: "all", l: "All" },
            { k: "active", l: "Active only" },
            { k: "paused", l: "Paused" },
          ].map((opt) => (
            <button
              key={opt.k}
              onClick={() => setStatusFilter(opt.k)}
              className={`text-[10px] px-2 py-0.5 rounded ${
                statusFilter === opt.k ? "bg-[#FFEB00] text-black font-semibold" : "bg-[#27272A] text-[#A1A1AA]"
              }`}
              data-testid={`status-filter-${opt.k}`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-1">
          <button onClick={expandAll} className="text-[10px] px-2 py-0.5 rounded bg-[#27272A] text-[#A1A1AA]">
            Expand all
          </button>
          <button onClick={collapseAll} className="text-[10px] px-2 py-0.5 rounded bg-[#27272A] text-[#A1A1AA]">
            Collapse
          </button>
        </div>
      </div>

      {/* Tree Table */}
      <div className="card-flat overflow-x-auto" style={{ minHeight: 200 }}>
        {loading ? (
          <div className="p-8 text-center text-[#71717A] text-xs">Loading…</div>
        ) : !data ? (
          <div className="p-8 text-center text-xs">
            <div className="text-[#A1A1AA] mb-2">No campaigns synced yet for {month}.</div>
            {!readOnly && (
              <button
                onClick={sync}
                disabled={syncing}
                className="px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: "#1877F2", color: "#fff" }}
              >
                Sync from Meta now
              </button>
            )}
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="p-8 text-center text-[#71717A] text-xs">No campaigns match the current filters.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#0F0F0F] sticky top-0 z-10">
              <tr className="border-b border-[#2A2A2A] text-[#71717A]">
                <th className="text-left px-2 py-2 font-semibold" style={{ minWidth: 380 }}>
                  Campaign / Ad Set / Ad
                </th>
                <th className="text-left px-2 py-2 font-semibold whitespace-nowrap">Status</th>
                <th className="text-left px-2 py-2 font-semibold whitespace-nowrap">Objective / Goal</th>
                {METRIC_COLS.map((col) => (
                  <SortHeader key={col.key} col={col} sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                ))}
                {SHOPIFY_COLS.map((col) => (
                  <SortHeader key={col.key} col={col} sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                ))}
                <th className="px-2 py-2 font-semibold whitespace-nowrap" style={{ width: 60 }}>Links</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map((c) => {
                const cKey = `c:${c.id}`;
                const cOpen = expanded.has(cKey);
                const adSets = (c.ad_sets || []).slice().sort(sortFn);
                return (
                  <React.Fragment key={c.id}>
                    <tr className="border-b border-[#1F1F22] hover:bg-[#161618]" data-testid="campaign-row">
                      <td className="px-2 py-2">
                        <div className="flex items-start gap-1.5">
                          <RowChevron open={cOpen} onClick={() => toggle(cKey)} hasChildren={adSets.length > 0} />
                          <div className="min-w-0">
                            <div className="font-semibold truncate" title={c.name}>
                              {c.name || "(unnamed)"}
                            </div>
                            <div className="text-[10px] text-[#71717A]">ID {c.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2"><StatusPill status={c.effective_status || c.status} /></td>
                      <td className="px-2 py-2 text-[#A1A1AA] text-[11px] whitespace-nowrap">{c.objective || "—"}</td>
                      {METRIC_COLS.map((col) => (
                        <td key={col.key} className="text-right data-number text-xs whitespace-nowrap px-2">
                          {renderMetric((c.metrics || {})[col.key], col.type, currency)}
                        </td>
                      ))}
                      <ShopifyCells entity={c} currency={currency} />
                      <td className="px-2 py-2 text-center">
                        <OpenInMeta url={metaUrl("campaign", c.id)} />
                      </td>
                    </tr>
                    {cOpen && adSets.map((s) => {
                      const sKey = `s:${s.id}`;
                      const sOpen = expanded.has(sKey);
                      const ads = (s.ads || []).slice().sort(sortFn);
                      return (
                        <React.Fragment key={s.id}>
                          <tr className="border-b border-[#1F1F22] bg-[#0E0E10]" data-testid="adset-row">
                            <td className="px-2 py-2">
                              <div className="flex items-start gap-1.5 pl-5">
                                <RowChevron open={sOpen} onClick={() => toggle(sKey)} hasChildren={ads.length > 0} />
                                <div className="min-w-0">
                                  <div className="text-[#E4E4E7] truncate" title={s.name}>
                                    <span className="text-[10px] text-[#71717A] mr-1">AdSet:</span>
                                    {s.name || "(unnamed)"}
                                  </div>
                                  <div className="text-[10px] text-[#71717A]">{s.optimization_goal || "—"} · {s.billing_event || "—"}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-2 py-2"><StatusPill status={s.effective_status || s.status} /></td>
                            <td className="px-2 py-2 text-[#A1A1AA] text-[11px] whitespace-nowrap">{s.optimization_goal || "—"}</td>
                            {METRIC_COLS.map((col) => (
                              <td key={col.key} className="text-right data-number text-xs whitespace-nowrap px-2">
                                {renderMetric((s.metrics || {})[col.key], col.type, currency)}
                              </td>
                            ))}
                            <ShopifyCells entity={s} currency={currency} />
                            <td className="px-2 py-2 text-center">
                              <OpenInMeta url={metaUrl("adset", s.id)} />
                            </td>
                          </tr>
                          {sOpen && ads.map((ad) => {
                            const cr = ad.creative || {};
                            const fmt = cr.format || ad.creative_type;
                            const thumb = cr.thumbnail_url || ad.thumbnail_url;
                            const dest = cr.destination_url;
                            const mapping = ad.mapping || {};
                            const sh = ad.shopify || {};
                            const product = sh.product;
                            return (
                              <tr key={ad.id} className="border-b border-[#1F1F22] bg-[#0A0A0C]" data-testid="ad-row">
                                <td className="px-2 py-2">
                                  <div className="flex items-start gap-2 pl-10">
                                    <Thumbnail url={thumb} format={fmt} size={44} />
                                    <div className="min-w-0">
                                      <div className="text-[#D4D4D8] truncate flex items-center gap-1.5" title={ad.name}>
                                        <span className="text-[10px] text-[#71717A]">Ad:</span>
                                        <span className="truncate">{ad.name || "(unnamed)"}</span>
                                        <FormatBadge fmt={fmt} />
                                        {mapping.confidence && <ConfidenceBadge c={mapping.confidence} />}
                                      </div>
                                      {(cr.title || cr.body) && (
                                        <div className="text-[10px] text-[#71717A] truncate max-w-md" title={cr.body || cr.title}>
                                          {cr.title ? <span className="text-[#A1A1AA]">{cr.title} · </span> : null}
                                          {cr.body || ""}
                                        </div>
                                      )}
                                      {product && (
                                        <div className="text-[10px] text-[#10B981] truncate max-w-md flex items-center gap-1" title={product.title}>
                                          {product.image && <img src={product.image} alt="" className="w-4 h-4 rounded object-cover" />}
                                          <span className="truncate">{product.title}</span>
                                        </div>
                                      )}
                                      {!product && sh.collection && (
                                        <div className="text-[10px] text-[#FCD34D] truncate max-w-md flex items-center gap-1" title={sh.collection.title}>
                                          {sh.collection.image && <img src={sh.collection.image} alt="" className="w-4 h-4 rounded object-cover" />}
                                          <span className="truncate">Collection: {sh.collection.title} ({sh.collection.products_count || 0} products)</span>
                                        </div>
                                      )}
                                      <div className="flex items-center gap-2 mt-0.5">
                                        {cr.cta_type && (
                                          <span className="text-[9px] text-[#52525B] uppercase tracking-wide">{cr.cta_type.replace(/_/g, " ")}</span>
                                        )}
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setPickerAdId(ad.id); }}
                                          className="text-[9px] text-[#71717A] hover:text-[#FFEB00] underline"
                                          data-testid="pick-product-btn"
                                        >
                                          {product ? "Change product" : "Tag product"}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 py-2"><StatusPill status={ad.effective_status || ad.status} /></td>
                                <td className="px-2 py-2 text-[#A1A1AA] text-[11px]">{mapping.type ? mapping.type.toUpperCase() : "—"}</td>
                                {METRIC_COLS.map((col) => (
                                  <td key={col.key} className="text-right data-number text-xs whitespace-nowrap px-2">
                                    {renderMetric((ad.metrics || {})[col.key], col.type, currency)}
                                  </td>
                                ))}
                                <ShopifyCells entity={ad} currency={currency} />
                                <td className="px-2 py-2 text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <OpenInMeta url={metaUrl("ad", ad.id)} />
                                    <OpenLandingPage url={dest} />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </>)}
      {pickerAdId && (
        <ProductPicker
          clientId={clientId}
          onCancel={() => setPickerAdId(null)}
          onPick={(p) => saveManualMapping(pickerAdId, p)}
        />
      )}
    </div>
  );
};

const ProductPicker = ({ clientId, onCancel, onPick }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      setSearching(true);
      try {
        const res = await axios.get(`${API}/clients/${clientId}/shopify/products/search`, { params: { q, limit: 25 } });
        setResults(res.data?.products || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, clientId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onCancel}>
      <div className="bg-[#0F0F11] border border-[#27272A] rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="product-picker-modal">
        <div className="p-4 border-b border-[#27272A]">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">Tag Shopify Product</h4>
            <button onClick={onCancel} className="text-[#71717A] hover:text-white text-xs">✕ Close</button>
          </div>
          <input
            autoFocus
            placeholder="Search Shopify products by title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="input-default text-xs py-2 px-3 w-full"
            data-testid="product-picker-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {searching ? (
            <div className="p-4 text-center text-[#71717A] text-xs">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-[#71717A] text-xs">{q ? "No products found" : "Type to search products"}</div>
          ) : (
            <ul className="divide-y divide-[#27272A]">
              {results.map((p) => (
                <li
                  key={p.id}
                  onClick={() => onPick(p)}
                  className="flex items-center gap-3 p-2 hover:bg-[#161618] cursor-pointer rounded"
                  data-testid="product-picker-item"
                >
                  {p.image ? (
                    <img src={p.image} alt="" className="w-10 h-10 rounded object-cover border border-[#27272A]" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-[#1F1F22] flex items-center justify-center">
                      <ImageIcon size={16} className="text-[#52525B]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-[#E4E4E7] truncate">{p.title}</div>
                    <div className="text-[10px] text-[#71717A] truncate">/{p.handle} {p.product_type ? ` · ${p.product_type}` : ""}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientAdCampaigns;
